import dayjs from "dayjs";

import { getMetricTimeStamp } from "../../../Helper/DatePicker/DateConverter";
import { toTitleCase } from "../../../Helper/Formatters/TextFormatter";

// Pure helpers behind the Network list's filter panel, column picker and CSV
// export. Kept out of the component and exported so they can be unit tested
// directly, per the repo's "test the pure function, don't mount the page" rule.

export const FILTER_KEY_LOCATION = "orgLocationMasterIDs";
export const FILTER_KEY_UPDATED = "updatedAt";

// Which filters an entity type gets.
//
// Driven by the type's own properties rather than a fixed list: only a type
// that declares a `location` property can be filtered by one, and offering the
// filter to a Vendor -- which has no location column -- would be a control that
// always returns everything.
//
// Every other declared property is a free-text identifier already covered by
// the search bar, so there is deliberately nothing else here.
export const buildFilterDefs = (entityType, locationOptions = []) => {
    const filters = {};
    const hasLocation = (entityType?.properties || []).some(
        (p) => p.type === "location"
    );

    if (hasLocation) {
        const locationProperty = (entityType?.properties || []).find(
            (p) => p.type === "location"
        );
        filters[FILTER_KEY_LOCATION] = {
            key: FILTER_KEY_LOCATION,
            label: locationProperty?.label || "Location",
            displayType: "multiSelectDropdown",
            values: locationOptions,
        };
    }

    filters[FILTER_KEY_UPDATED] = {
        key: FILTER_KEY_UPDATED,
        label: "Last Updated",
        displayType: "dateCalender",
        values: [],
    };

    return filters;
};

// Panel selection -> request params.
//
// The same object feeds the list and the export, so a downloaded file always
// covers exactly the rows the table was showing.
export const mapSelectedFiltersToParams = (selectedFilters = {}) => {
    const params = {};

    const locations = selectedFilters[FILTER_KEY_LOCATION];
    if (locations?.length) {
        // The backend takes ids; a non-numeric selection means the option list
        // and the selection have drifted, and passing it through would 400 the
        // whole list rather than ignoring one bad value.
        const ids = locations
            .map((v) => parseInt(v, 10))
            .filter((v) => !isNaN(v));
        if (ids.length) params.orgLocationMasterIDs = ids;
    }

    const rawDate = selectedFilters[FILTER_KEY_UPDATED]?.[0];
    if (rawDate) {
        try {
            const parsed = JSON.parse(rawDate);
            const from = parsed.startDateUTC || parsed.startDate;
            const to = parsed.endDateUTC || parsed.endDate;
            if (from) params.updatedFrom = from;
            if (to) params.updatedTo = to;
        } catch (e) {
            /* a malformed date filter narrows nothing rather than 400ing */
        }
    }

    return params;
};

// Which columns start visible. `defaultVisible: false` in the column config
// opts a column out of the initial set without removing it from the picker.
// Returns dataIndex values, NOT config keys. TableColumns is written entirely
// against dataIndex -- its checkboxes, its deselect-all and its "modified" dot
// all compare against column.dataIndex -- so a config whose key and dataIndex
// differ would render every box unchecked and, on the first Apply, write
// dataIndex values into a list being matched by key. The table would then show
// only the actions column and the CSV would be rows of empty objects.
export const getDefaultSelectedColumns = (columnsConfig, columnOrdering) =>
    (columnOrdering || [])
        .filter(
            (key) =>
                columnsConfig?.[key] &&
                columnsConfig[key].valueRenderingType !== "action-menu" &&
                columnsConfig[key].defaultVisible !== false
        )
        .map((key) => columnsConfig[key].dataIndex || key);

// One cell, formatted for the CSV exactly as the table renders it.
//
// This exists to satisfy the repo's Data Transform Parity Rule. The export
// endpoint returns the SAME flat records the list returns -- raw, unformatted --
// and the formatting happens here, from the same column config the table
// renders with.
//
// Every branch below mirrors a case in TableColumnValueRenderer. When a
// renderer is added there and a config starts using it, add it here too: the
// module is config-driven, so a new column type arrives with no code change and
// would otherwise land in the file raw while the screen shows it formatted.
export const formatCellForCSV = (column, record) => {
    const raw = record?.[column?.dataIndex];
    const type = column?.valueRenderingType;
    const isBlank = raw === null || raw === undefined || raw === "";

    if (type === "timestamp") {
        // Timestamp.jsx falls back to "Not Available", not "", when no
        // defaultValue is configured -- matched here so the same column cannot
        // read one way on screen and another in the file.
        const fallback = column?.defaultValue || "Not Available";
        if (isBlank || raw === "0000-00-00 00:00:00") return fallback;
        const format = column?.dateFormat || "DD MMM'YY hh:mm A";
        // disableTimezone skips the CONVERSION, not the formatting -- the cell
        // still runs the value through dayjs with the column's format.
        return column?.disableTimezone
            ? dayjs(raw, "YYYY-MM-DD HH:mm:ss").format(format)
            : getMetricTimeStamp(raw, format);
    }

    if (isBlank) {
        // The CSV writer emits "" for a missing key, so a column with an
        // explicit placeholder should show that placeholder instead.
        return column?.defaultValue ?? "";
    }

    if (type === "number") {
        // toFixed only when it IS a number, as the renderer does -- a numeric
        // string is shown as the default value there, not coerced.
        return typeof raw === "number"
            ? raw.toFixed(column?.decimalPlaces || 0)
            : column?.defaultValue ?? raw;
    }

    if (column?.textFormat === "title") return toTitleCase(String(raw));
    if (column?.textFormat === "snake") {
        return String(raw).trim().toLowerCase().replace(/\s+/g, "_");
    }

    return raw;
};


// Records -> CSV rows keyed by headerLabel.
//
// headerLabel, not dataIndex: GenericDraggableTable's onDownloadCSV reorders
// the rows by `columnOrder`, which is a list of header labels, and any key it
// cannot find becomes an empty column.
export const buildCSVRows = (records = [], columns = []) => {
    const exportable = columns.filter(
        (c) => c?.valueRenderingType !== "action-menu" && c?.dataIndex
    );
    return records.map((record) =>
        Object.fromEntries(
            exportable.map((column) => [
                column.headerLabel || column.title,
                formatCellForCSV(column, record),
            ])
        )
    );
};
